import { EMR_SECTION_IDS } from 'src/mentor/components/MentorPrescriptionTemplates/components/utils/emrSectionIds'

/**
 * Single source of truth for the per-section "professional" text colours the prescription
 * renders in colored mode. Used by BOTH the printed/preview renderer (PrescriptionViewComponent)
 * and the template editor's per-section colour pickers, so the pickers pre-fill with exactly the
 * colour the preview shows when a section hasn't overridden its colour.
 */
export const PROFESSIONAL_SECTION_COLORS = {
  patientDetails: { color: '#1e293b', headingColor: '#0f172a' }, // Slate
  vitals: { color: '#1e293b', headingColor: '#0f172a' }, // Slate
  diagnosis: { color: '#1e40af', headingColor: '#1e3a8a' }, // Blue
  observations: { color: '#dc2626', headingColor: '#b91c1c' }, // Red
  complaints: { color: '#059669', headingColor: '#047857' }, // Emerald
  drugAdvice: { color: '#0369a1', headingColor: '#075985' }, // Sky Blue
  homunculus: { color: '#d97706', headingColor: '#b45309' }, // Amber
  advice: { color: '#7c3aed', headingColor: '#6d28d9' }, // Violet
  investigations: { color: '#c026d3', headingColor: '#a21caf' }, // Fuchsia
  selectedTherapy: { color: '#2563eb', headingColor: '#1d4ed8' }, // Blue
  suggestedTherapy: { color: '#ea580c', headingColor: '#c2410c' }, // Orange
  instructions: { color: '#dc2626', headingColor: '#b91c1c' }, // Red
  referredToDoctorName: { color: '#64748b', headingColor: '#475569' }, // Slate
  header: { color: '#1e293b', headingColor: '#0f172a' }, // Slate
  footer: { color: '#1e293b', headingColor: '#0f172a' }, // Slate
  nextVisitAndSignature: { color: '#1e293b', headingColor: '#0f172a' }, // Slate
  bodyColor: { color: '#1e293b', headingColor: '#0f172a' }, // Slate
  patientHistoryNotes: { color: '#1e293b', headingColor: '#0f172a' }, // Slate
}

export const DEFAULT_SECTION_TEXT_COLOR = '#1e293b'

/** EMR section id → palette colour key (+ render order index used for alternate borders). */
export const EMR_SECTION_COLOR_MAP = {
  [EMR_SECTION_IDS.PATIENT_VITALS]: { colorKey: 'vitals', orderIndex: 0 },
  [EMR_SECTION_IDS.OBSERVATIONS]: { colorKey: 'observations', orderIndex: 1 },
  [EMR_SECTION_IDS.HEALTH_COMPLAINTS]: { colorKey: 'complaints', orderIndex: 2 },
  [EMR_SECTION_IDS.DIAGNOSIS]: { colorKey: 'diagnosis', orderIndex: 3 },
  [EMR_SECTION_IDS.DRUG_ADVICE]: { colorKey: 'drugAdvice', orderIndex: 4 },
  [EMR_SECTION_IDS.ADVICE]: { colorKey: 'advice', orderIndex: 5 },
  [EMR_SECTION_IDS.INVESTIGATIONS]: { colorKey: 'investigations', orderIndex: 6 },
  [EMR_SECTION_IDS.PSYCH_ASSESSMENTS]: { colorKey: 'selectedTherapy', orderIndex: 7 },
  [EMR_SECTION_IDS.PATIENT_HISTORY_NOTES]: { colorKey: 'patientHistoryNotes', orderIndex: 8 },
  [EMR_SECTION_IDS.THERAPY_RECOMMENDATION]: { colorKey: 'selectedTherapy', orderIndex: 9 },
  [EMR_SECTION_IDS.JOINT_HOMUNCULUS]: { colorKey: 'homunculus', orderIndex: 10 },
  [EMR_SECTION_IDS.INSTRUCTIONS]: { colorKey: 'instructions', orderIndex: 11 },
  [EMR_SECTION_IDS.REFER_TO]: { colorKey: 'referredToDoctorName', orderIndex: 12 },
  [EMR_SECTION_IDS.NEXT_VISIT_DETAILS]: { colorKey: 'nextVisitAndSignature', orderIndex: 13 },
  [EMR_SECTION_IDS.SIGNATURE]: { colorKey: 'nextVisitAndSignature', orderIndex: 14 },
}

/** Professional colour for a palette colour key + colour type ('color' | 'headingColor'). */
export function getProfessionalSectionColor(colorKey, colorType = 'color', colorMode = 'colored') {
  if (colorMode === 'bw') return '#000000'
  return PROFESSIONAL_SECTION_COLORS[colorKey]?.[colorType] || DEFAULT_SECTION_TEXT_COLOR
}

/**
 * The effective default colours a section's text renders in (matching the preview) when neither
 * the template nor the practitioner-wide settings override them. `keyColor` (the item name) has no
 * dedicated palette entry — on screen it inherits the section's body `color`, so we mirror that.
 */
export function getDefaultSectionStyle(sectionId, colorMode = 'colored') {
  const colorKey = EMR_SECTION_COLOR_MAP[sectionId]?.colorKey
  // Unmapped sections get no palette colour in the preview (they render inherited/near-black), so
  // don't impose one on the picker — let it fall back to its own default.
  if (!colorKey) return {}
  const color = getProfessionalSectionColor(colorKey, 'color', colorMode)
  const headingColor = getProfessionalSectionColor(colorKey, 'headingColor', colorMode)
  return { color, headingColor, keyColor: color }
}
